import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GroupDatesService } from '../../../services/group-dates.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CustomerListDataService } from '../../../services/customer-list-data.service';
import { PreferencesService } from '../../../services/preferences.service';
import { CustomerService } from '../../../services/customer.service';
import { ToastrService } from 'ngx-toastr';

// Filter interfaces
interface FilterState {
  // Basic Information
  religion: string[];
  ageMin: number | null;
  ageMax: number | null;
  nationality: string[];
  maritalStatus: string[];
  gender: string;

  // Personality & Lifestyle
  interest: string[];
  values: string[];
  alcoholConsumption: string[];
  smoker: string[];
  children: string[];
  iUsuallyWakeUpAt: string[];
  hobbies: string[];
  fitness: string[];
  personality: string[];
  lifeGoals: string[];
  maxionGoals: string[];
  meetVibes: string[];

  // Signup & Profile Status
  profileStepNo: { min: number | null; max: number | null };
  signupStepNo: { min: number | null; max: number | null };
  passportVerified: string;
  subscriptionStatus: string[];

  // Activity-Based Filters
  noMatchesInLastDays: number;
}

@Component({
  selector: 'app-group-attendees',
  templateUrl: './group-attendees.component.html',
  styleUrls: ['./group-attendees.component.css'],
})
export class GroupAttendeesComponent implements OnInit {
  attendees: any[] = [];
  maleAttendees: any[] = [];
  femaleAttendees: any[] = [];
  confirmedAttendees: any[] = [];
  cancelledAttendees: any[] = [];
  loading: boolean = false;
  error: string = '';
  groupDateId: string = '';
  groupDate: any = {};
  prev_date_id: string = '';
  next_date_id: string = '';
  selectedAttendeeId: string = '';
  actionType: string = '';
  notificationForm: FormGroup;
  submitted = false;
  activeTab: string = 'Male'; // Default to Male tab
  activeConfirmedTab: string = 'Confirmed'; // Default to Confirmed tab
  isConfirmedModalOpen = false;
  isSavingOrder: boolean = false;
  savingGender: string = '';
  originalSortOrders: { [key: string]: number } = {}; // Store original sort orders
  selectedUser: any = null;
  currentUserIndex: number = 0;
  totalUsers: number = 0;
  currentAttendeesList: any[] = [];
  isConfirmingInvites: boolean = false;
  isOpeningGroupDate: boolean = false;
  isClosingGroupDate: boolean = false;
  isConfirmingGroupDate: boolean = false;

  // Search and filter properties
  search: string = '';
  debounceTimer: any = null;
  showFilters: boolean = false;
  activeFilterSection: string = '';
  selectedGender: string = ''; // No default selection
  selectedFilters: FilterState = {
    religion: [],
    ageMin: null,
    ageMax: null,
    nationality: [],
    maritalStatus: [],
    gender: '',
    interest: [],
    values: [],
    alcoholConsumption: [],
    smoker: [],
    children: [],
    iUsuallyWakeUpAt: [],
    profileStepNo: { min: null, max: null },
    signupStepNo: { min: null, max: null },
    passportVerified: '',
    subscriptionStatus: [],
    noMatchesInLastDays: 0,
    hobbies: [],
    fitness: [],
    personality: [],
    lifeGoals: [],
    maxionGoals: [],
    meetVibes: [],
  };

  // Filter options
  religionOptions = [
    'Agnostic',
    'Atheist',
    'Buddhist',
    'Catholic',
    'Christian',
    'Hindu',
    'Jewish',
    'Muslim',
    'Sikh',
    'Spiritual',
    'Prefer not to say',
    'Other',
  ];

  nationalityOptions: any[] = [];
  maritalStatusOptions = ['Never Married', 'Married', 'Divorced', 'Separated'];
  genderOptions = ['Male', 'Female'];
  interestOptions: any[] = [];
  valueOptions: any[] = [];
  alcoholConsumptionOptions: any[] = [];
  smokingOptions: any[] = [];
  childrenOptions = ['Has Children', 'No Children'];
  wakeupAtOptions: any[] = [];
  hobbiesOptions: any[] = [];
  fitnessOptions: any[] = [];
  personalityOptions: any[] = [];
  lifeGoalsOptions: any[] = [];
  maxionGoalsOptions: any[] = [];
  meetVibesOptions: any[] = [];
  subscriptionStatusOptions = [
    'Expired',
    'Paid',
    'Unpaid',
    'Scouted_ADMIN',
    'Scouted_FRIEND',
    'Cancelled',
  ];

  // Filter chips for display
  activeFilterChips: Array<{
    key: string;
    label: string;
    value: string[] | string;
  }> = [];

  // Search results
  searchResults: any[] = [];
  searchLoading: boolean = false;
  searchCount: number = 0;
  searchPage: number = 1;
  searchTotal: number = 10;
  selectedUsers: string[] = []; // Array to store selected user IDs

  // Pagination properties
  searchPageSize: number = 10;
  searchTotalPages: number = 0;
  searchHasNextPage: boolean = false;
  searchHasPrevPage: boolean = false;

  // Make Math available in template
  Math = Math;

  // User detail navigation properties
  next_user_id: number | undefined;
  prev_user_id: number | undefined;
  userDetailData: any = null;
  personalDetails: any;
  aboutYourself: any;
  userImages: any;
  info: any;
  datingInfo: any;
  profileStatus: any;
  socialDetails: any;
  userSubscription: any;
  referralCount: any;
  marketStatus: any;
  isSearchUser: boolean = false;
  isInvitedUser: boolean = false;

  activeInvitedTab: string = 'Male'; // Default to Male tab

  // Invited attendees properties
  invitedAttendees: any[] = [];
  maleInvitedAttendees: any[] = [];
  femaleInvitedAttendees: any[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private groupDatesService: GroupDatesService,
    private formBuilder: FormBuilder,
    private customerDataService: CustomerListDataService,
    private preferencesService: PreferencesService,
    private customerService: CustomerService,
    private toastr: ToastrService
  ) {
    this.notificationForm = this.formBuilder.group({
      title: ['', Validators.required],
      body: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.groupDateId = this.route.snapshot.paramMap.get('id') || '';
    if (this.groupDateId) {
      this.loadAttendees();
      this.loadFilterOptions();
    }
    // Initialize gender filter (no default selection - shows "All")
    this.selectedFilters.gender = this.selectedGender;
    this.updateFilterChips();
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.notificationForm.controls;
  }

  loadFilterOptions(): void {
    const token = localStorage['token'];

    this.preferencesService
      .getAllMasterDataOptions(token)
      .subscribe((res: any) => {
        if (res.status === 200 && res.data) {
          // Load interests from hobbies table
          if (res.data.hobbies) {
            this.interestOptions = res.data.hobbies;
          }

          // Load values from value table
          if (res.data.value) {
            this.valueOptions = res.data.value;
          }

          // Load alcohol consumption options
          if (res.data.alcohol_consumption) {
            this.alcoholConsumptionOptions = res.data.alcohol_consumption;
          }

          // Load smoking options from smoker table
          if (res.data.smoker) {
            this.smokingOptions = res.data.smoker;
          }

          // Load wake up time options from i_usually_wake_up_at table
          if (res.data.i_usually_wake_up_at) {
            this.wakeupAtOptions = res.data.i_usually_wake_up_at;
          }

          // Load hobbies options (same as interests for now, but separate filter)
          if (res.data.hobbies) {
            this.hobbiesOptions = res.data.hobbies;
          }

          // Load fitness options
          if (res.data.fitness) {
            this.fitnessOptions = res.data.fitness;
          }

          // Load personality options
          if (res.data.personality) {
            this.personalityOptions = res.data.personality;
          }

          // Load life goals options
          if (res.data.life_goals) {
            this.lifeGoalsOptions = res.data.life_goals;
          }

          // Load maxion goals options
          if (res.data.maxion_goals) {
            this.maxionGoalsOptions = res.data.maxion_goals;
          }

          // Load meet vibes options
          if (res.data.meet_vibes) {
            this.meetVibesOptions = res.data.meet_vibes;
          }

          // Load nationality options from country list
          if (res.data.nationalities) {
            this.nationalityOptions = res.data.nationalities.map(
              (item: any) => item.name
            );
          }
        }
      });
  }

  loadAttendees(): void {
    this.loading = true;
    this.error = '';

    this.groupDatesService
      .getGroupDateApplicants({ id: this.groupDateId })
      .subscribe({
        next: (response) => {
          // First, separate confirmed and cancelled attendees
          this.confirmedAttendees =
            response.data?.applicants?.filter(
              (attendee: any) => attendee.status === 'Confirmed'
            ) || [];

          this.cancelledAttendees =
            response.data?.applicants?.filter(
              (attendee: any) => attendee.status === 'Cancelled'
            ) || [];

          // Separate invited attendees (status: 'Invited')
          this.invitedAttendees =
            response.data?.applicants?.filter(
              (attendee: any) => attendee.status === 'Invited'
            ) || [];

          // Separate invited attendees by gender
          this.maleInvitedAttendees = this.invitedAttendees
            .filter((attendee: any) => attendee.info?.gender === 'Male')
            .sort((a: any, b: any) => a.sort_order - b.sort_order);

          this.femaleInvitedAttendees = this.invitedAttendees
            .filter((attendee: any) => attendee.info?.gender === 'Female')
            .sort((a: any, b: any) => a.sort_order - b.sort_order);

          // Then separate remaining attendees by gender (waitlisted)
          const waitlistedAttendees =
            response.data?.applicants?.filter(
              (attendee: any) => attendee.status === 'Waitlisted'
            ) || [];

          this.maleAttendees = waitlistedAttendees
            .filter((attendee: any) => attendee.info?.gender === 'Male')
            .sort((a: any, b: any) => a.sort_order - b.sort_order);

          this.femaleAttendees = waitlistedAttendees
            .filter((attendee: any) => attendee.info?.gender === 'Female')
            .sort((a: any, b: any) => a.sort_order - b.sort_order);

          // Store original sort orders
          this.originalSortOrders = {};
          [...this.maleAttendees, ...this.femaleAttendees].forEach(
            (attendee) => {
              this.originalSortOrders[attendee.id] = attendee.sort_order;
            }
          );

          this.groupDate = response.data?.group_date || {};

          this.loading = false;
        },
        error: (error) => {
          this.error = 'Failed to load attendees. Please try again later.';
          this.loading = false;
          console.error('Error loading attendees:', error);
        },
      });
  }

  // Search and filter methods
  onKeyUpEvent(event: any) {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.search = event.target.value;
      this.searchUsers();
    }, 500);
  }

  searchUsers(): void {
    this.searchLoading = true;
    const cleanedFilters = this.cleanFilters(this.selectedFilters);

    let obj = {
      limit: this.searchPageSize,
      offset: this.searchPage - 1,
      search: this.search,
      action: 'approved', // Only search approved users
      ...cleanedFilters,
    };

    const token = localStorage['token'];
    this.customerDataService
      .getCustomerList(obj, token)
      .subscribe((res: any) => {
        if (res && res.status === 200) {
          this.searchCount = this.customerDataService.getCount();
          this.searchResults = this.customerDataService.getUserList();

          // Calculate pagination
          this.searchTotalPages = Math.ceil(
            this.searchCount / this.searchPageSize
          );
          this.searchHasNextPage = this.searchPage < this.searchTotalPages;
          this.searchHasPrevPage = this.searchPage > 1;
        } else {
          this.toastr.error(res.message || 'Failed to search users');
        }
        this.searchLoading = false;
      });
  }

  // Filter methods
  toggleFilters() {
    this.showFilters = !this.showFilters;
    if (!this.showFilters) {
      this.activeFilterSection = '';
    }
  }

  toggleFilterSection(section: string) {
    this.activeFilterSection =
      this.activeFilterSection === section ? '' : section;
  }

  private parseNumber(value: string): number | null {
    if (value === null || value.trim() === '') {
      return null;
    }
    const num = parseInt(value, 10);
    return isNaN(num) ? null : num;
  }

  // Range input methods
  applyAgeRangeFilter(data: { min: string; max: string }) {
    this.selectedFilters.ageMin = this.parseNumber(data.min);
    this.selectedFilters.ageMax = this.parseNumber(data.max);
    this.updateFilterChips();
  }

  applyStepRangeFilter(data: { type: string; min: string; max: string }) {
    const minVal = this.parseNumber(data.min);
    const maxVal = this.parseNumber(data.max);

    if (data.type === 'profile') {
      this.selectedFilters.profileStepNo = { min: minVal, max: maxVal };
    } else {
      this.selectedFilters.signupStepNo = { min: minVal, max: maxVal };
    }
    this.updateFilterChips();
  }

  // Single select methods
  updateSingleSelect(data: { key: string; event: Event }) {
    const field = data.key as keyof FilterState;
    const value = (data.event.target as HTMLSelectElement).value;
    (this.selectedFilters as any)[field] = value;
    this.updateFilterChips();
  }

  // Multi select methods
  updateMultiSelect(data: { key: string; value: string; event: Event }) {
    const field = data.key as keyof FilterState;
    const checked = (data.event.target as HTMLInputElement).checked;
    const currentArray = (this.selectedFilters as any)[field] as any[];

    if (checked) {
      // Find the complete option object to store both id and name
      let optionToAdd: any = data.value; // Default to the value (for simple string options)

      // For options that have id/name structure, find the complete object
      const optionArrays = {
        interest: this.interestOptions,
        values: this.valueOptions,
        alcoholConsumption: this.alcoholConsumptionOptions,
        smoker: this.smokingOptions,
        iUsuallyWakeUpAt: this.wakeupAtOptions,
        hobbies: this.hobbiesOptions,
        fitness: this.fitnessOptions,
        personality: this.personalityOptions,
        lifeGoals: this.lifeGoalsOptions,
        maxionGoals: this.maxionGoalsOptions,
        meetVibes: this.meetVibesOptions,
      };

      const optionsArray = optionArrays[field as keyof typeof optionArrays];
      if (optionsArray) {
        const foundOption = optionsArray.find(
          (item: any) => item.id == data.value
        );
        if (foundOption) {
          optionToAdd = foundOption; // Store the complete object with id and name
        }
      }

      // Check if already exists (compare by id if object, or by value if string)
      const exists = currentArray.some((item: any) => {
        if (typeof item === 'object' && item.id) {
          return item.id == data.value;
        }
        return item === data.value;
      });

      if (!exists) {
        currentArray.push(optionToAdd);
      }
    } else {
      // Remove by id if object, or by value if string
      const index = currentArray.findIndex((item: any) => {
        if (typeof item === 'object' && item.id) {
          return item.id == data.value;
        }
        return item === data.value;
      });

      if (index > -1) {
        currentArray.splice(index, 1);
      }
    }

    this.updateFilterChips();
  }

  // Number input methods
  updateNumberInput(data: { key: string; event: Event }) {
    const field = data.key as keyof FilterState;
    const value = (data.event.target as HTMLInputElement).value;
    (this.selectedFilters as any)[field] = this.parseNumber(value) || 0;
    this.updateFilterChips();
  }

  // Apply filters
  applyFilters() {
    this.searchPage = 1; // Reset to first page
    this.searchUsers();
    this.showFilters = false;
    this.activeFilterSection = '';
  }

  // Gender filter method
  setGenderFilter(gender: string) {
    this.selectedGender = gender;
    this.selectedFilters.gender = gender;
    this.updateFilterChips();
    this.applyFilters();
  }

  // Pagination methods
  goToPage(page: number): void {
    if (
      page >= 1 &&
      page <= this.searchTotalPages &&
      page !== this.searchPage
    ) {
      this.searchPage = page;
      this.searchUsers();
    }
  }

  nextPage(): void {
    if (this.searchHasNextPage) {
      this.searchPage++;
      this.searchUsers();
    }
  }

  prevPage(): void {
    if (this.searchHasPrevPage) {
      this.searchPage--;
      this.searchUsers();
      // Note: selectedUsers array is preserved across pagination
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(
      1,
      this.searchPage - Math.floor(maxVisiblePages / 2)
    );
    let endPage = Math.min(
      this.searchTotalPages,
      startPage + maxVisiblePages - 1
    );

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  getPaginationInfo(): string {
    if (this.searchCount === 0) {
      return 'No results found';
    }

    const start =
      this.searchResults.length > 0
        ? (this.searchPage - 1) * this.searchPageSize + 1
        : 0;
    const end =
      this.searchResults.length > 0
        ? Math.min(this.searchPage * this.searchPageSize, this.searchCount)
        : 0;

    if (start === 0 && end === 0) {
      return `Page ${this.searchPage} of ${this.searchTotalPages} (no results on this page)`;
    }

    return `Showing ${start} to ${end} of ${this.searchCount} results`;
  }

  // Clear all filters
  clearAllFilters(isApiCallRequired: boolean = true) {
    this.selectedFilters = {
      religion: [],
      ageMin: null,
      ageMax: null,
      nationality: [],
      maritalStatus: [],
      gender: '',
      interest: [],
      values: [],
      alcoholConsumption: [],
      smoker: [],
      children: [],
      iUsuallyWakeUpAt: [],
      profileStepNo: { min: null, max: null },
      signupStepNo: { min: null, max: null },
      passportVerified: '',
      subscriptionStatus: [],
      noMatchesInLastDays: 0,
      hobbies: [],
      fitness: [],
      personality: [],
      lifeGoals: [],
      maxionGoals: [],
      meetVibes: [],
    };
    this.selectedGender = ''; // Reset gender selection to "All"
    this.activeFilterChips = [];
    this.searchPage = 1;
    this.searchTotalPages = 0;
    this.searchHasNextPage = false;
    this.searchHasPrevPage = false;
    if (isApiCallRequired) {
      this.searchUsers();
    }
  }

  // Clear individual filter chip
  clearFilterChip(chip: {
    key: string;
    label: string;
    value: string[] | string;
  }) {
    switch (chip.key) {
      case 'religion':
        this.selectedFilters.religion = [];
        break;
      case 'ageRange':
        this.selectedFilters.ageMin = null;
        this.selectedFilters.ageMax = null;
        break;
      case 'nationality':
        this.selectedFilters.nationality = [];
        break;
      case 'maritalStatus':
        this.selectedFilters.maritalStatus = [];
        break;
      case 'gender':
        this.selectedFilters.gender = '';
        this.selectedGender = ''; // Reset gender selection to "All"
        break;
      case 'interest':
        this.selectedFilters.interest = [];
        break;
      case 'values':
        this.selectedFilters.values = [];
        break;
      case 'alcoholConsumption':
        this.selectedFilters.alcoholConsumption = [];
        break;
      case 'smoker':
        this.selectedFilters.smoker = [];
        break;
      case 'children':
        this.selectedFilters.children = [];
        break;
      case 'iUsuallyWakeUpAt':
        this.selectedFilters.iUsuallyWakeUpAt = [];
        break;
      case 'profileStepNo':
        this.selectedFilters.profileStepNo = { min: null, max: null };
        break;
      case 'signupStepNo':
        this.selectedFilters.signupStepNo = { min: null, max: null };
        break;
      case 'passportVerified':
        this.selectedFilters.passportVerified = '';
        break;
      case 'subscriptionStatus':
        this.selectedFilters.subscriptionStatus = [];
        break;
      case 'noMatchesInLastDays':
        this.selectedFilters.noMatchesInLastDays = 0;
        break;
      case 'hobbies':
        this.selectedFilters.hobbies = [];
        break;
      case 'fitness':
        this.selectedFilters.fitness = [];
        break;
      case 'personality':
        this.selectedFilters.personality = [];
        break;
      case 'lifeGoals':
        this.selectedFilters.lifeGoals = [];
        break;
      case 'maxionGoals':
        this.selectedFilters.maxionGoals = [];
        break;
      case 'meetVibes':
        this.selectedFilters.meetVibes = [];
        break;
    }
    this.updateFilterChips();
    this.searchPage = 1;
    this.searchTotalPages = 0;
    this.searchHasNextPage = false;
    this.searchHasPrevPage = false;
    this.searchUsers();
  }

  // Update filter chips for display
  updateFilterChips() {
    this.activeFilterChips = [];

    // Religion
    if (
      this.selectedFilters.religion &&
      this.selectedFilters.religion.length > 0
    ) {
      this.activeFilterChips.push({
        key: 'religion',
        label: 'Religion',
        value: this.selectedFilters.religion,
      });
    }

    // Age Range
    if (
      this.selectedFilters.ageMin !== null ||
      this.selectedFilters.ageMax !== null
    ) {
      this.activeFilterChips.push({
        key: 'ageRange',
        label: 'Age',
        value: `${this.selectedFilters.ageMin ?? 'Any'} - ${
          this.selectedFilters.ageMax ?? 'Any'
        }`,
      });
    }

    // Nationality
    if (
      this.selectedFilters.nationality &&
      this.selectedFilters.nationality.length > 0
    ) {
      this.activeFilterChips.push({
        key: 'nationality',
        label: 'Nationality',
        value: this.selectedFilters.nationality,
      });
    }

    // Marital Status
    if (
      this.selectedFilters.maritalStatus &&
      this.selectedFilters.maritalStatus.length > 0
    ) {
      this.activeFilterChips.push({
        key: 'maritalStatus',
        label: 'Marital Status',
        value: this.selectedFilters.maritalStatus,
      });
    }

    // Gender
    if (
      this.selectedFilters.gender &&
      this.selectedFilters.gender.trim() !== ''
    ) {
      this.activeFilterChips.push({
        key: 'gender',
        label: 'Gender',
        value: this.selectedFilters.gender,
      });
    }

    // Interests
    if (
      this.selectedFilters.interest &&
      this.selectedFilters.interest.length > 0
    ) {
      const selectedNames = this.selectedFilters.interest.map((item: any) => {
        return typeof item === 'object' && item.name ? item.name : item;
      });
      this.activeFilterChips.push({
        key: 'interest',
        label: 'Interests',
        value: selectedNames,
      });
    }

    // Values
    if (this.selectedFilters.values && this.selectedFilters.values.length > 0) {
      const selectedNames = this.selectedFilters.values.map((item: any) => {
        return typeof item === 'object' && item.name ? item.name : item;
      });
      this.activeFilterChips.push({
        key: 'values',
        label: 'Values',
        value: selectedNames,
      });
    }

    // Alcohol Consumption
    if (
      this.selectedFilters.alcoholConsumption &&
      this.selectedFilters.alcoholConsumption.length > 0
    ) {
      const selectedNames = this.selectedFilters.alcoholConsumption.map(
        (item: any) => {
          return typeof item === 'object' && item.name ? item.name : item;
        }
      );
      this.activeFilterChips.push({
        key: 'alcoholConsumption',
        label: 'Alcohol',
        value: selectedNames,
      });
    }

    // Smoking
    if (this.selectedFilters.smoker && this.selectedFilters.smoker.length > 0) {
      const selectedNames = this.selectedFilters.smoker.map((item: any) => {
        return typeof item === 'object' && item.name ? item.name : item;
      });
      this.activeFilterChips.push({
        key: 'smoker',
        label: 'Smoking',
        value: selectedNames,
      });
    }

    // Children
    if (
      this.selectedFilters.children &&
      this.selectedFilters.children.length > 0
    ) {
      this.activeFilterChips.push({
        key: 'children',
        label: 'Children',
        value: this.selectedFilters.children,
      });
    }

    // Wakeup At
    if (
      this.selectedFilters.iUsuallyWakeUpAt &&
      this.selectedFilters.iUsuallyWakeUpAt.length > 0
    ) {
      const selectedNames = this.selectedFilters.iUsuallyWakeUpAt.map(
        (item: any) => {
          return typeof item === 'object' && item.name ? item.name : item;
        }
      );
      this.activeFilterChips.push({
        key: 'iUsuallyWakeUpAt',
        label: 'Wakeup Time',
        value: selectedNames,
      });
    }

    // Profile Step No
    if (
      this.selectedFilters.profileStepNo.min !== null ||
      this.selectedFilters.profileStepNo.max !== null
    ) {
      this.activeFilterChips.push({
        key: 'profileStepNo',
        label: 'Profile Step',
        value: `${this.selectedFilters.profileStepNo.min ?? 'Any'} - ${
          this.selectedFilters.profileStepNo.max ?? 'Any'
        }`,
      });
    }

    // Signup Step No
    if (
      this.selectedFilters.signupStepNo.min !== null ||
      this.selectedFilters.signupStepNo.max !== null
    ) {
      this.activeFilterChips.push({
        key: 'signupStepNo',
        label: 'Signup Step',
        value: `${this.selectedFilters.signupStepNo.min ?? 'Any'} - ${
          this.selectedFilters.signupStepNo.max ?? 'Any'
        }`,
      });
    }

    // Passport Verified
    if (
      this.selectedFilters.passportVerified &&
      this.selectedFilters.passportVerified.trim() !== ''
    ) {
      this.activeFilterChips.push({
        key: 'passportVerified',
        label: 'Passport',
        value: this.selectedFilters.passportVerified,
      });
    }

    // Subscription Status
    if (
      this.selectedFilters.subscriptionStatus &&
      this.selectedFilters.subscriptionStatus.length > 0
    ) {
      this.activeFilterChips.push({
        key: 'subscriptionStatus',
        label: 'Subscription',
        value: this.selectedFilters.subscriptionStatus,
      });
    }

    // No Matches in Last Days
    if (
      this.selectedFilters.noMatchesInLastDays &&
      this.selectedFilters.noMatchesInLastDays > 0
    ) {
      this.activeFilterChips.push({
        key: 'noMatchesInLastDays',
        label: 'No Matches',
        value: `${this.selectedFilters.noMatchesInLastDays} days`,
      });
    }

    // Hobbies
    if (
      this.selectedFilters.hobbies &&
      this.selectedFilters.hobbies.length > 0
    ) {
      const selectedNames = this.selectedFilters.hobbies.map((item: any) => {
        return typeof item === 'object' && item.name ? item.name : item;
      });
      this.activeFilterChips.push({
        key: 'hobbies',
        label: 'Hobbies',
        value: selectedNames,
      });
    }

    // Fitness
    if (
      this.selectedFilters.fitness &&
      this.selectedFilters.fitness.length > 0
    ) {
      const selectedNames = this.selectedFilters.fitness.map((item: any) => {
        return typeof item === 'object' && item.name ? item.name : item;
      });
      this.activeFilterChips.push({
        key: 'fitness',
        label: 'Fitness',
        value: selectedNames,
      });
    }

    // Personality
    if (
      this.selectedFilters.personality &&
      this.selectedFilters.personality.length > 0
    ) {
      const selectedNames = this.selectedFilters.personality.map(
        (item: any) => {
          return typeof item === 'object' && item.name ? item.name : item;
        }
      );
      this.activeFilterChips.push({
        key: 'personality',
        label: 'Personality',
        value: selectedNames,
      });
    }

    // Life Goals
    if (
      this.selectedFilters.lifeGoals &&
      this.selectedFilters.lifeGoals.length > 0
    ) {
      const selectedNames = this.selectedFilters.lifeGoals.map((item: any) => {
        return typeof item === 'object' && item.name ? item.name : item;
      });
      this.activeFilterChips.push({
        key: 'lifeGoals',
        label: 'Life Goals',
        value: selectedNames,
      });
    }

    // Maxion Goals
    if (
      this.selectedFilters.maxionGoals &&
      this.selectedFilters.maxionGoals.length > 0
    ) {
      const selectedNames = this.selectedFilters.maxionGoals.map(
        (item: any) => {
          return typeof item === 'object' && item.name ? item.name : item;
        }
      );
      this.activeFilterChips.push({
        key: 'maxionGoals',
        label: 'Maxion Goals',
        value: selectedNames,
      });
    }

    // Meet Vibes
    if (
      this.selectedFilters.meetVibes &&
      this.selectedFilters.meetVibes.length > 0
    ) {
      const selectedNames = this.selectedFilters.meetVibes.map((item: any) => {
        return typeof item === 'object' && item.name ? item.name : item;
      });
      this.activeFilterChips.push({
        key: 'meetVibes',
        label: 'Meet Vibes',
        value: selectedNames,
      });
    }
  }

  // Check if user is already in waitlist
  isUserInWaitlist(userId: string): boolean {
    const allWaitlistUsers = [...this.maleAttendees, ...this.femaleAttendees];
    return allWaitlistUsers.some((attendee) => attendee.user_id === userId);
  }

  // Check if user is in confirmed or cancelled list
  getUserStatus(userId: string): string | null {
    const confirmedUser = this.confirmedAttendees.find(
      (attendee) => attendee.user_id === userId
    );
    if (confirmedUser) {
      return 'Confirmed';
    }

    const cancelledUser = this.cancelledAttendees.find(
      (attendee) => attendee.user_id === userId
    );
    if (cancelledUser) {
      return 'Cancelled';
    }

    return null;
  }

  // Check if user is in invited list
  isUserInvited(userId: string): boolean {
    return this.invitedAttendees.some(
      (attendee) => attendee.user_id === userId
    );
  }

  // Remove invited user from search results
  removeInvitedUserFromSearch(user: any): void {
    // Find the invited attendee
    const invitedAttendee = this.invitedAttendees.find(
      (attendee) => attendee.user_id === user.id
    );

    if (invitedAttendee) {
      // Use the same endpoint as other removals
      this.groupDatesService
        .updateAttendeeStatus({
          group_date_id: this.groupDateId,
          user_id: invitedAttendee.user_id,
          status: 'Removed',
        })
        .subscribe({
          next: (response) => {
            // Update local lists immediately
            this.removeUserFromInvitedList(invitedAttendee);
            this.toastr.success('User removed from invited list successfully');
            // Don't call searchUsers() - handle updates locally
          },
          error: (error) => {
            this.toastr.error('Failed to remove user from invited list');
            console.error('Error removing invited user:', error);
          },
        });
    }
  }

  // Add user to waitlist
  inviteUserToDate(user: any, gender: string): void {
    const obj = {
      group_date_id: this.groupDateId,
      user_ids: [user.id], // Pass as array of user IDs
      gender: gender,
    };

    this.groupDatesService.inviteUserToDate(obj).subscribe({
      next: (response) => {
        console.log('Invite response:', response);

        // Use the actual attendee data returned from the backend
        if (response?.data?.attendees && response.data.attendees.length > 0) {
          const attendee = response.data.attendees[0]; // Single user invite
          console.log('attendee', response.data.attendees);
          this.addUserToInvitedListWithRealData(attendee, user);
        }

        this.toastr.success('User invited to date successfully');
        // Don't call searchUsers() - handle updates locally
      },
      error: (error) => {
        this.toastr.error(
          error?.error?.message || 'Failed to invite user to date'
        );
        console.error('Error inviting user to date:', error);
      },
    });
  }

  // Helper method to add user to invited list locally with real attendee data
  private addUserToInvitedListWithRealData(attendee: any, user: any): void {
    const invitedUser = {
      id: attendee.id, // Use the real attendee ID from backend
      user_id: user.id,
      status: 'Invited',
      info: {
        ...user,
      },
      user_created_at: user.created_at || new Date().toISOString(),
      sort_order: attendee.sort_order || 1,
    };

    // Add to appropriate gender list
    if (user.gender === 'Male') {
      this.maleInvitedAttendees.push(invitedUser);
    } else {
      this.femaleInvitedAttendees.push(invitedUser);
    }

    // Add to main invited list
    this.invitedAttendees.push(invitedUser);
  }

  // Remove user from waitlist
  removeUserFromWaitlist(user: any): void {
    // Find the attendee in the waitlist
    const allWaitlistUsers = [...this.maleAttendees, ...this.femaleAttendees];
    const attendee = allWaitlistUsers.find(
      (attendee) => attendee.user_id === user.id
    );

    if (attendee) {
      this.selectedAttendeeId = attendee.id;
      this.removeAttendee();
    } else {
      this.toastr.error('User not found in waitlist');
    }
  }

  // Handle checkbox selection
  toggleUserSelection(userId: string): void {
    const index = this.selectedUsers.indexOf(userId);
    if (index > -1) {
      this.selectedUsers.splice(index, 1);
    } else {
      this.selectedUsers.push(userId);
    }
  }

  // Check if user is selected
  isUserSelected(userId: string): boolean {
    return this.selectedUsers.includes(userId);
  }

  // Check if user can be selected (not in waitlist, confirmed, or cancelled)
  canSelectUser(userId: string): boolean {
    return !this.isUserInWaitlist(userId) && !this.getUserStatus(userId);
  }

  // Select all available users from current page
  selectAllUsers(): void {
    this.selectedUsers = [];
    this.searchResults.forEach((user) => {
      if (this.canSelectUser(user.id)) {
        this.selectedUsers.push(user.id);
      }
    });
  }

  // Deselect all users
  deselectAllUsers(): void {
    this.selectedUsers = [];
  }

  // Check if all available users are selected
  areAllUsersSelected(): boolean {
    const availableUsers = this.searchResults.filter((user) =>
      this.canSelectUser(user.id)
    );
    return (
      availableUsers.length > 0 &&
      availableUsers.every((user) => this.selectedUsers.includes(user.id))
    );
  }

  // Add multiple users to waitlist
  addSelectedUsersToWaitlist(): void {
    if (this.selectedUsers.length === 0) {
      this.toastr.warning('Please select at least one user');
      return;
    }

    // Add male users
    if (this.selectedUsers.length > 0) {
      const maleObj = {
        group_date_id: this.groupDateId,
        user_ids: this.selectedUsers,
      };
      this.inviteBulkUsersToDate(maleObj);
    }
  }

  // Helper method to add bulk users
  private inviteBulkUsersToDate(obj: any): void {
    this.groupDatesService.inviteUserToDate(obj).subscribe({
      next: (response) => {
        console.log('Bulk invite response:', response);

        // Use the actual attendee data returned from the backend
        if (response?.data?.attendees && response.data.attendees.length > 0) {
          this.addBulkUsersToInvitedListWithRealData(
            response.data.attendees,
            obj.user_ids
          );
        }

        this.toastr.success(
          `${obj.user_ids.length} user(s) invited to date successfully`
        );
      },
      error: (error) => {
        this.toastr.error(
          error?.error?.message || 'Failed to invite users to date'
        );
        console.error('Error inviting users to date:', error);
      },
      complete: () => {
        // Don't call searchUsers() - handle updates locally
        this.selectedUsers = []; // Clear selection
      },
    });
  }

  // Helper method to add bulk users to invited list locally with real attendee data
  private addBulkUsersToInvitedListWithRealData(
    attendees: any[],
    userIds: string[]
  ): void {
    attendees.forEach((attendee) => {
      const user = this.searchResults.find((u) => u.id === attendee.user_id);
      if (user) {
        this.addUserToInvitedListWithRealData(attendee, user);
      }
    });
  }

  // Tab selection method for gender tabs
  selectTab(tab: string): void {
    this.activeTab = tab;
  }

  // Tab selection method for confirmed/cancelled tabs
  selectConfirmedTab(tab: string): void {
    this.activeConfirmedTab = tab;
  }

  // Tab selection method for invited users tabs
  selectInvitedTab(tab: string): void {
    this.activeInvitedTab = tab;
  }

  // Get current invited attendees list based on active tab
  getCurrentInvitedAttendeesList(): any[] {
    switch (this.activeInvitedTab) {
      case 'Male':
        return this.maleInvitedAttendees;
      case 'Female':
        return this.femaleInvitedAttendees;
      default:
        return this.maleInvitedAttendees;
    }
  }

  // Count methods for displaying totals
  getConfirmedAttendeesCount(): number {
    return this.confirmedAttendees.length;
  }

  getCancelledAttendeesCount(): number {
    return this.cancelledAttendees.length;
  }

  getInvitedAttendeesCount(): number {
    return this.invitedAttendees.length;
  }

  getMaleInvitedAttendeesCount(): number {
    return this.maleInvitedAttendees.length;
  }

  getFemaleInvitedAttendeesCount(): number {
    return this.femaleInvitedAttendees.length;
  }

  getWaitlistedAttendeesCount(): number {
    return this.maleAttendees.length + this.femaleAttendees.length;
  }

  getMaleWaitlistedAttendeesCount(): number {
    return this.maleAttendees.length;
  }

  getFemaleWaitlistedAttendeesCount(): number {
    return this.femaleAttendees.length;
  }

  // Helper methods for step management
  getCurrentStep(): number {
    if (!this.groupDate?.isInvitationSent) return 1; // Send Invites
    if (this.groupDate?.status === 'Upcoming') return 2; // Open Group Date
    if (this.groupDate?.status === 'Open') return 3; // Close Group Date
    if (this.groupDate?.status === 'Closed') return 4; // Confirm Group Date
    if (this.groupDate?.status === 'Confirmed') return 5; // Completed
    return 6; // Completed
  }

  canSendInvites(): boolean {
    return this.getCurrentStep() === 1;
  }

  canOpenGroupDate(): boolean {
    return this.getCurrentStep() === 2;
  }

  canCloseGroupDate(): boolean {
    return this.getCurrentStep() === 3;
  }

  canConfirmGroupDate(): boolean {
    return this.getCurrentStep() === 4;
  }

  getStepDescription(): string {
    const step = this.getCurrentStep();
    switch (step) {
      case 1:
        return 'Step 1: Send Invites';
      case 2:
        return 'Step 2: Open Group Date';
      case 3:
        return 'Step 3: Close Group Date';
      case 4:
        return 'Step 4: Confirm Group Date';
      case 5:
        return 'Completed';
      default:
        return 'Unknown Step';
    }
  }

  // Confirm attendee invites
  confirmInvites(): void {
    if (this.getInvitedAttendeesCount() === 0) {
      this.toastr.warning('No invited users to confirm');
      return;
    }

    if (
      confirm(
        'Are you sure you want to confirm all invited users? This action cannot be undone.'
      )
    ) {
      this.isConfirmingInvites = true;

      this.groupDatesService
        .confirmAttendeeInvites(this.groupDateId)
        .subscribe({
          next: (response) => {
            this.isConfirmingInvites = false;
            this.groupDate.isInvitationSent = true;
            this.toastr.success('Invites confirmed successfully');
          },
          error: (error) => {
            this.isConfirmingInvites = false;
            this.toastr.error('Failed to confirm invites. Please try again.');
            console.error('Error confirming invites:', error);
          },
        });
    }
  }

  // Open group date
  openGroupDate(): void {
    if (confirm('Are you sure you want to open this group date?')) {
      this.isOpeningGroupDate = true;

      this.groupDatesService
        .updateGroupDateStatus(this.groupDateId, 'Open')
        .subscribe({
          next: (response) => {
            this.isOpeningGroupDate = false;
            this.toastr.success('Group date opened successfully');
            this.groupDate.status = 'Open';
          },
          error: (error) => {
            this.isOpeningGroupDate = false;
            this.toastr.error('Failed to open group date. Please try again.');
            console.error('Error opening group date:', error);
          },
        });
    }
  }

  // Close group date
  closeGroupDate(): void {
    if (confirm('Are you sure you want to close this group date?')) {
      this.isClosingGroupDate = true;

      this.groupDatesService
        .updateGroupDateStatus(this.groupDateId, 'Closed')
        .subscribe({
          next: (response) => {
            this.isClosingGroupDate = false;
            this.toastr.success('Group date closed successfully');
            this.groupDate.status = 'Closed';
          },
          error: (error) => {
            this.isClosingGroupDate = false;
            this.toastr.error('Failed to close group date. Please try again.');
            console.error('Error closing group date:', error);
          },
        });
    }
  }

  // Close search results section
  closeSearchResults(): void {
    this.search = '';
    this.searchResults = [];
    this.searchCount = 0;
    this.selectedUsers = [];
    this.searchPage = 1;
    this.searchTotalPages = 0;
    this.searchHasNextPage = false;
    this.searchHasPrevPage = false;

    // Clear all filters
    this.clearAllFilters(false);
  }

  // Move invited user to waitlist
  moveToWaitlist(attendee: any): void {
    if (confirm('Are you sure you want to move this user to the waitlist?')) {
      // Update the status from 'Invited' to 'Waitlisted'
      const updateData = {
        group_date_id: this.groupDateId,
        user_id: attendee.user_id,
        status: 'Waitlisted',
      };

      this.groupDatesService
        .updateGroupDateApplicantStatus(updateData)
        .subscribe({
          next: (response) => {
            // Update local lists immediately
            this.moveUserFromInvitedToWaitlist(attendee);
            this.toastr.success('User moved to waitlist successfully');
          },
          error: (error) => {
            this.toastr.error('Failed to move user to waitlist');
            console.error('Error moving user to waitlist:', error);
          },
        });
    }
  }

  // Helper method to move user from invited to waitlist locally
  private moveUserFromInvitedToWaitlist(attendee: any): void {
    // Remove from invited lists
    this.removeUserFromInvitedList(attendee);

    // Add to waitlist
    const waitlistUser = {
      ...attendee,
      status: 'Waitlisted',
    };

    if (attendee.info?.gender === 'Male') {
      this.maleAttendees.push(waitlistUser);
    } else {
      this.femaleAttendees.push(waitlistUser);
    }
  }

  // Remove invited user
  removeInvitedUser(attendee: any): void {
    // Use the same endpoint as waitlist removal
    this.groupDatesService
      .updateAttendeeStatus({
        group_date_id: this.groupDateId,
        user_id: attendee.user_id || attendee.id,
        status: 'Removed',
      })
      .subscribe({
        next: (response) => {
          // Update local lists immediately
          this.removeUserFromInvitedList(attendee);
          this.toastr.success('User removed from invited list successfully');

          // Handle navigation if called from user details modal
          this.handleNavigationAfterRemoval(attendee);
        },
        error: (error) => {
          this.toastr.error('Failed to remove user from invited list');
          console.error('Error removing invited user:', error);
        },
      });
  }

  // Handle navigation after removing a user from the modal
  private handleNavigationAfterRemoval(removedAttendee: any): void {
    // Check if we're in the user details modal
    if (this.isInvitedUser && this.currentAttendeesList.length > 0) {
      // Remove the user from the current attendees list
      this.currentAttendeesList = this.currentAttendeesList.filter(
        (attendee) =>
          attendee.user_id !== (removedAttendee.user_id || removedAttendee.id)
      );

      // Update total users count
      this.totalUsers = this.currentAttendeesList.length;

      if (this.totalUsers === 0) {
        // No users left, close the modal
        this.closeUserDetailsModal();
      } else {
        // Navigate to next user if available, otherwise previous
        if (this.canNavigateNext()) {
          this.navigateUser('next');
        } else if (this.canNavigatePrev()) {
          this.navigateUser('prev');
        } else {
          // Only one user left, update the selected user
          this.selectedUser = { ...this.currentAttendeesList[0] };
          this.currentUserIndex = 0;
          this.selectedAttendeeId = this.selectedUser.id;

          // Load detailed user information
          const userId = this.selectedUser.user_id || this.selectedUser.id;
          if (userId) {
            this.loadUserDetail(userId);
          }
        }
      }
    }
  }

  // Close user details modal
  private closeUserDetailsModal(): void {
    const modal = document.getElementById('userDetailsModal');
    if (modal) {
      // @ts-ignore
      $('#userDetailsModal').modal('hide');
    }
  }

  // Helper method to remove user from invited list locally
  private removeUserFromInvitedList(attendee: any): void {
    // Remove from main invited list
    this.invitedAttendees = this.invitedAttendees.filter(
      (user) => user.user_id !== (attendee.user_id || attendee.id)
    );

    // Remove from gender-specific lists
    this.maleInvitedAttendees = this.maleInvitedAttendees.filter(
      (user) => user.user_id !== (attendee.user_id || attendee.id)
    );
    this.femaleInvitedAttendees = this.femaleInvitedAttendees.filter(
      (user) => user.user_id !== (attendee.user_id || attendee.id)
    );
  }

  moveToPrevDate(): void {
    if (this.prev_date_id) {
      this.router.navigate(['/group-dates', this.prev_date_id, 'attendees']);
    }
  }

  moveToNextDate(): void {
    if (this.next_date_id) {
      this.router.navigate(['/group-dates', this.next_date_id, 'attendees']);
    }
  }

  removeAttendee(): void {
    this.groupDatesService
      .updateAttendeeStatus({
        group_date_id: this.groupDateId,
        user_id: this.selectedUser.user_id,
        status: 'Removed',
      })
      .subscribe({
        next: (response) => {
          console.log('Attendee removed successfully:', response);
          // Close modal
          const modal = document.getElementById('removeAttendeeModal');
          if (modal) {
            // @ts-ignore
            $('#removeAttendeeModal').modal('hide');
          }
          // Reload attendees
          this.loadAttendees();
        },
        error: (error) => {
          console.error('Error removing attendee:', error);
        },
      });
  }

  openConfirmGroupDateModal(): void {
    // @ts-ignore
    $('#confirmGroupDateModal').modal('show');
  }

  confirmGroupDate(): void {
    this.groupDatesService.confirmGroupDate(this.groupDateId).subscribe({
      next: (response: any) => {
        console.log('Group date confirmed successfully:', response);
        // Close modal
        const modal = document.getElementById('confirmGroupDateModal');
        if (modal) {
          // @ts-ignore
          $('#confirmGroupDateModal').modal('hide');
        }
        // Reload attendees to update status
        this.groupDate.status = 'Confirmed';
        this.loadAttendees();
      },
      error: (error: any) => {
        console.error('Error confirming group date:', error);
      },
    });
  }

  openRemoveAttendeeModal(attendee: any): void {
    this.selectedAttendeeId = attendee.id;
    // @ts-ignore
    $('#removeAttendeeModal').modal('show');
  }

  saveAttendeeOrder(gender: string): void {
    // Set loading state
    this.isSavingOrder = true;
    this.savingGender = gender;

    // Get the appropriate list based on gender
    const attendeesList =
      gender === 'Male' ? this.maleAttendees : this.femaleAttendees;

    // Validate that no sort_order is null
    const invalidAttendees = attendeesList.filter(
      (attendee) =>
        attendee.sort_order === null || attendee.sort_order === undefined
    );

    if (invalidAttendees.length > 0) {
      const invalidEmails = invalidAttendees
        .map((attendee) => attendee.info.email)
        .join(', ');
      alert(
        `Please enter a valid sort order for the following ${gender} attendees:\n${invalidEmails}`
      );
      this.isSavingOrder = false;
      this.savingGender = '';
      return;
    }

    // Filter only modified attendees
    const modifiedAttendees = attendeesList.filter(
      (attendee) => this.originalSortOrders[attendee.id] !== attendee.sort_order
    );

    if (modifiedAttendees.length === 0) {
      alert('No changes detected in sort order.');
      this.isSavingOrder = false;
      this.savingGender = '';
      return;
    }

    // Prepare the data for the API call
    const orderData = {
      group_date_id: this.groupDateId,
      attendees: modifiedAttendees.map((a) => ({
        id: a.id,
        sort_order: a.sort_order,
      })),
    };

    if (orderData?.attendees?.length > 0) {
      // Call the service to update the attendee order
      this.groupDatesService.updateAttendeeOrder(orderData).subscribe({
        next: (response) => {
          console.log(
            `${gender} attendee order updated successfully:`,
            response
          );
          // Update original sort orders after successful save
          modifiedAttendees.forEach((attendee) => {
            this.originalSortOrders[attendee.id] = attendee.sort_order;
          });
          this.loadAttendees(); // Reload the attendees to ensure data is in sync
        },
        error: (error) => {
          console.error(`Error updating ${gender} attendee order:`, error);
        },
        complete: () => {
          // Reset loading state
          this.isSavingOrder = false;
          this.savingGender = '';
        },
      });
    }
  }

  openUserDetailsModal(attendee: any, tab: string): void {
    switch (tab) {
      case 'Confirmed':
      case 'Cancelled':
        this.currentAttendeesList =
          tab === 'Confirmed'
            ? this.confirmedAttendees
            : this.cancelledAttendees;
        this.isConfirmedModalOpen = true;
        this.isSearchUser = false;
        this.isInvitedUser = false;
        break;
      case 'Male':
      case 'Female':
        this.currentAttendeesList =
          tab === 'Male' ? this.maleAttendees : this.femaleAttendees;
        this.isConfirmedModalOpen = false;
        this.isSearchUser = false;
        this.isInvitedUser = false;
        break;
      case 'InvitedMale':
      case 'InvitedFemale':
        this.currentAttendeesList =
          tab === 'InvitedMale'
            ? this.maleInvitedAttendees
            : this.femaleInvitedAttendees;
        this.isConfirmedModalOpen = false;
        this.isSearchUser = false;
        this.isInvitedUser = true;
        break;
      case 'search':
        // For search results, we don't have navigation since it's a single user view
        this.currentAttendeesList = [attendee];
        this.isConfirmedModalOpen = false;
        this.isSearchUser = true;
        this.isInvitedUser = false;
        break;
      default:
        this.currentAttendeesList = [];
        this.isConfirmedModalOpen = false;
        this.isSearchUser = false;
        this.isInvitedUser = false;
    }

    this.currentUserIndex = this.currentAttendeesList.findIndex(
      (a) => a.id === attendee.id
    );
    this.totalUsers = this.currentAttendeesList.length;
    this.selectedUser = { ...attendee };
    this.selectedAttendeeId = attendee.id;

    // Load detailed user information using the same endpoint as customer-detail
    this.loadUserDetail(attendee.user_id || attendee.id);

    // @ts-ignore
    $('#userDetailsModal').modal('show');
  }

  navigateUser(direction: 'prev' | 'next'): void {
    if (direction === 'prev' && this.canNavigatePrev()) {
      this.currentUserIndex--;
    } else if (direction === 'next' && this.canNavigateNext()) {
      this.currentUserIndex++;
    }
    this.selectedUser = { ...this.currentAttendeesList[this.currentUserIndex] };

    // Load detailed user information for the new user
    const userId = this.selectedUser.user_id || this.selectedUser.id;
    if (userId) {
      this.loadUserDetail(userId);
    }
  }

  canNavigatePrev(): boolean {
    return this.currentUserIndex > 0 && this.totalUsers > 1;
  }

  canNavigateNext(): boolean {
    return this.currentUserIndex < this.totalUsers - 1 && this.totalUsers > 1;
  }

  private updateAttendeeList(
    attendeeList: any[],
    attendeeId: string,
    newSortOrder: number
  ): void {
    const index = attendeeList.findIndex((a) => a.id === attendeeId);
    if (index !== -1) {
      attendeeList[index].sort_order = newSortOrder;
      // Update original sort order
      this.originalSortOrders[attendeeId] = newSortOrder;
      // Sort the list
      attendeeList.sort((a, b) => a.sort_order - b.sort_order);
    }
  }

  saveOrder(): void {
    if (
      !this.selectedUser ||
      !this.selectedUser.sort_order ||
      this.selectedUser.sort_order < 1
    ) {
      alert('Please enter a valid sort order (minimum 1)');
      return;
    }

    this.isSavingOrder = true;

    // Prepare the data for the API call
    const orderData = {
      group_date_id: this.groupDateId,
      attendees: [
        {
          id: this.selectedUser.id,
          sort_order: this.selectedUser.sort_order,
        },
      ],
    };

    // Call the service to update the attendee order
    this.groupDatesService.updateAttendeeOrder(orderData).subscribe({
      next: (response) => {
        console.log('Attendee order updated successfully:', response);
        // Update the correct list based on gender
        if (this.selectedUser.info.gender === 'Male') {
          this.updateAttendeeList(
            this.maleAttendees,
            this.selectedUser.id,
            this.selectedUser.sort_order
          );
        } else if (this.selectedUser.info.gender === 'Female') {
          this.updateAttendeeList(
            this.femaleAttendees,
            this.selectedUser.id,
            this.selectedUser.sort_order
          );
        }
      },
      error: (error) => {
        console.error('Error updating attendee order:', error);
        alert('Failed to update sort order. Please try again.');
        this.isSavingOrder = false;
      },
      complete: () => {
        this.isSavingOrder = false;
      },
    });
  }

  // User detail navigation methods
  moveToNextUser(): void {
    if (this.next_user_id) {
      this.loadUserDetail(this.next_user_id);
    }
  }

  moveToPrevUser(): void {
    if (this.prev_user_id) {
      this.loadUserDetail(this.prev_user_id);
    }
  }

  loadUserDetail(userId: number): void {
    const token = localStorage['token'];
    const filterParams = this.cleanFilters(this.selectedFilters);

    // Add search term if present
    if (this.search) {
      filterParams.search = this.search;
    }

    this.customerService
      .viewCustomerDetail(userId, filterParams, token)
      .subscribe({
        next: (res: any) => {
          if (res && res.status == 200) {
            this.userSubscription = res.data.subscription;
            this.referralCount = res.data.total_referral;
            this.aboutYourself = res.data.aboutYourself;
            this.userImages = res.data.uploads?.best_photos;
            this.personalDetails = res.data.personalDetails;
            this.marketStatus = res.data.subscribedEmail;
            this.info = res.data.info;
            this.datingInfo = res.data.datingData;
            this.next_user_id = res.data.next_user_id;
            this.prev_user_id = res.data.prev_user_id;
            this.profileStatus = res.data.idVerification;
            this.socialDetails = res.data.socialConnect;
            this.userDetailData = res.data;

            // Update the selectedUser with the new data
            this.selectedUser = {
              ...this.selectedUser,
              ...res.data,
            };

            console.log('User detail loaded:', res.data);
          } else {
            this.toastr.error('Failed to load user details');
          }
        },
        error: (error) => {
          console.error('Error loading user detail:', error);
          this.toastr.error('Failed to load user details');
        },
      });
  }

  // Clean up filters to remove empty values (same as customer-detail component)
  private cleanFilters(filters: FilterState): any {
    const cleaned: any = {};

    if (!filters) return cleaned;

    // Basic Information
    if (filters.religion) cleaned.religion = filters.religion;
    if (filters.nationality) cleaned.nationality = filters.nationality;
    if (filters.maritalStatus) cleaned.maritalStatus = filters.maritalStatus;
    if (filters.gender) cleaned.gender = filters.gender;

    // Age Range - use age_min and age_max instead of object
    if (filters.ageMin !== null) {
      cleaned.ageMin = filters.ageMin;
    }
    if (filters.ageMax !== null) {
      cleaned.ageMax = filters.ageMax;
    }

    // Personality & Lifestyle - extract IDs from objects
    if (filters.interest && Array.isArray(filters.interest)) {
      cleaned.interest = filters.interest.map((item: any) =>
        typeof item === 'object' && item.id ? item.id : item
      );
    }
    if (filters.values && Array.isArray(filters.values)) {
      cleaned.values = filters.values.map((item: any) =>
        typeof item === 'object' && item.id ? item.id : item
      );
    }
    if (
      filters.alcoholConsumption &&
      Array.isArray(filters.alcoholConsumption)
    ) {
      cleaned.alcoholConsumption = filters.alcoholConsumption.map(
        (item: any) => (typeof item === 'object' && item.id ? item.id : item)
      );
    }
    if (filters.smoker && Array.isArray(filters.smoker)) {
      cleaned.smoker = filters.smoker.map((item: any) =>
        typeof item === 'object' && item.id ? item.id : item
      );
    }
    if (filters.children) cleaned.children = filters.children;
    if (filters.iUsuallyWakeUpAt && Array.isArray(filters.iUsuallyWakeUpAt)) {
      cleaned.iUsuallyWakeUpAt = filters.iUsuallyWakeUpAt.map((item: any) =>
        typeof item === 'object' && item.id ? item.id : item
      );
    }
    if (filters.hobbies && Array.isArray(filters.hobbies)) {
      cleaned.hobbies = filters.hobbies.map((item: any) =>
        typeof item === 'object' && item.id ? item.id : item
      );
    }
    if (filters.fitness && Array.isArray(filters.fitness)) {
      cleaned.fitness = filters.fitness.map((item: any) =>
        typeof item === 'object' && item.id ? item.id : item
      );
    }
    if (filters.personality && Array.isArray(filters.personality)) {
      cleaned.personality = filters.personality.map((item: any) =>
        typeof item === 'object' && item.id ? item.id : item
      );
    }
    if (filters.lifeGoals && Array.isArray(filters.lifeGoals)) {
      cleaned.lifeGoals = filters.lifeGoals.map((item: any) =>
        typeof item === 'object' && item.id ? item.id : item
      );
    }
    if (filters.maxionGoals && Array.isArray(filters.maxionGoals)) {
      cleaned.maxionGoals = filters.maxionGoals.map((item: any) =>
        typeof item === 'object' && item.id ? item.id : item
      );
    }
    if (filters.meetVibes && Array.isArray(filters.meetVibes)) {
      cleaned.meetVibes = filters.meetVibes.map((item: any) =>
        typeof item === 'object' && item.id ? item.id : item
      );
    }

    // Profile & Signup Steps
    if (filters.profileStepNo.min !== null) {
      cleaned.profileStepMin = filters.profileStepNo.min;
    }
    if (filters.profileStepNo.max !== null) {
      cleaned.profileStepMax = filters.profileStepNo.max;
    }

    if (filters.signupStepNo.min !== null) {
      cleaned.signupStepMin = filters.signupStepNo.min;
    }
    if (filters.signupStepNo.max !== null) {
      cleaned.signupStepMax = filters.signupStepNo.max;
    }

    // Status filters
    if (filters.passportVerified)
      cleaned.passportVerified = filters.passportVerified;
    if (filters.subscriptionStatus)
      cleaned.subscriptionStatus = filters.subscriptionStatus;

    // Activity filters
    if (filters.noMatchesInLastDays > 0)
      cleaned.noMatchesInLastNDays = filters.noMatchesInLastDays;

    return cleaned;
  }

  // Helper methods for user detail display (same as customer-detail component)
  formatData(data: any[]): string {
    if (!data || data.length === 0) {
      return 'N/A';
    }
    const names = data.map((item) => item.name).join(', ');
    return names;
  }

  getImage(data: any[]): string {
    if (data && data.length > 0 && data[0].image) {
      return data[0].image;
    } else {
      // Return a sample image URL
      return 'assets/images/about-icon.svg';
    }
  }

  capitalizeFirstLetterOfEachWord(input: string): string {
    if (!input || input.trim().length === 0) {
      return 'N/A';
    }

    // Replace underscores with spaces
    const replacedSpaces = input.replace(/_/g, ' ');

    // Capitalize the first letter of each word
    const capitalizedWords = replacedSpaces.replace(
      /\w\S*/g,
      function (txt: any) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      }
    );
    return capitalizedWords;
  }

  formatDateOfBirth(dateOfBirth: string): string {
    if (!dateOfBirth) {
      return 'N/A';
    }

    try {
      const date = new Date(dateOfBirth);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'N/A';
      }

      // Format date as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      // Calculate age
      const today = new Date();
      let age = today.getFullYear() - date.getFullYear();
      const monthDiff = today.getMonth() - date.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < date.getDate())
      ) {
        age--;
      }

      return `${year}-${month}-${day} (${age} years)`;
    } catch (error) {
      return 'N/A';
    }
  }
}
